import React from 'react';
import { Body, Link, Box } from '@hover/blueprint';
import { IFaqQuestion } from '../interfaces/IFaqQuestion';

export const TRUST_CENTER_FAQ: Array<IFaqQuestion> = [
  {
    id: 0,
    title: 'How do I report security or unethical behavior concerns?',
    content: (
      <Body mb={500}>
        We monitor security activity on our pre-production and production
        systems. Incidents are managed using our Security Incident Management
        process. This process defines the assessment, containment, recovery, and
        remediation of issue resolution. All communication (internal and
        external) is managed by the Critical Response Team. HOVER users,
        Customers, Partners or other outside parties should report security
        issues or unethical behavior to{' '}
        <Link href="mailto:security@hover.to" fontWeight={400}>
          security@hover.to
        </Link>
        .
      </Body>
    ),
  },
  {
    id: 1,
    title:
      'Who controls which individuals can access the data in my HOVER account?',
    content: (
      <Box flexDir="column">
        <Body mb={500}>
          If you dwell within a property, you are the account administrator of
          the corresponding HOVER data. You have control over who has access to
          your account.
        </Body>
        <Body mb={500}>
          If you are part of a business organization with multiple users, each
          creating HOVER models on behalf of your customer residents, you will
          have an assigned HOVER account administrator. The account
          administrator has control over which individuals have access to the
          account. New users invited by the account administrator will follow an
          account sign-up process that includes setting up a password.
        </Body>
      </Box>
    ),
  },
  {
    id: 2,
    title: 'How do you protect my data?',
    content: (
      <Body mb={500}>
        We protect all data in transit and at rest using standard Transport
        Layer Security (TLS 1.2 or higher) and the Advanced Encryption Standard
        (AES-128) protocols. We maintain redundant backups with live-updated
        standby database systems to maximize system availability.
      </Body>
    ),
  },
  {
    id: 3,
    title: 'How do you maintain the quality and security of the HOVER product?',
    content: (
      <Box flexDir="column">
        <Body mb={500}>
          We use agile software development methodologies to constantly enhance
          your experience with HOVER. Our software engineering teams practice
          continuous delivery to provide quality software to our customers,
          potentially with multiple releases per day. Our dedicated Quality
          Assurance (QA) team ensures the effectiveness of our automated test
          suites, and supplements automation with manual QA when needed.
        </Body>
        <Body mb={500}>
          We use secure cloud infrastructure to deliver our service to you. Our
          intrusion detection, continuous penetration tests, and vulnerability
          management systems make sure you can use our services safely.
        </Body>
      </Box>
    ),
  },
  {
    id: 4,
    title: 'What are your data retention and deletion policies?',
    content: (
      <Box flexDir="column">
        <Body mb={500}>
          We do not delete your personal information unless you tell us to, and
          in some cases we must retain data to verify requests. We know that the
          HOVER data is a resource to you, the resident or future resident of
          your property. If you wish to have us remove your personal data from
          our systems and disable your accounts, you may send such a request to{' '}
          <Link href="mailto:privacy@hover.to" fontWeight={400}>
            privacy@hover.to
          </Link>
          .
        </Body>
        <Body mb={500}>
          We also maintain confidential system and security related logs for a
          minimum of three years. These logs help us monitor system performance
          and serve as a forensic record in the unlikely event of a security
          incident.
        </Body>
      </Box>
    ),
  },
  {
    id: 5,
    title: 'How do you ensure the security of HOVER’s business partners?',
    content: (
      <Body mb={500}>
        We conduct comprehensive security assessments and annual review on the
        critical vendors that help us deliver our service. We review service
        agreements and closely inspect conditions that may lead to misuse of
        critically important information. We hold our vendors accountable just
        as we are accountable to you.
      </Body>
    ),
  },
  {
    id: 6,
    title: 'Is HOVER SOC2 certified?',
    content: (
      <Body mb={500}>
        HOVER is SOC2 Type I compliant. HOVER’s current SOC2 report is available
        to customers and prospects under Non Disclosure Agreement (NDA).
        Coordinate with your HOVER account representative to request a copy of
        our SOC2 report.
      </Body>
    ),
  },
];

export const TRUST_CENTER_HEADER_DESCRIPTION: Array<IFaqQuestion> = [
  {
    id: 0,
    title: 'Your home, your data.',
    content: (
      <Body mb={500}>
        You’re in control of the personal data you provide, either directly or
        through a 3rd party. By using HOVER, you give us permission to do
        certain things with your data so we can run our service. Some of these
        operations may require us to send your data to our business partners —
        such as a cloud storage provider — that we’ve contracted with to provide
        parts of the HOVER Service. Before we do this, we’ll always make sure
        that our contracts with such partners protect your data rights.
      </Body>
    ),
  },
  {
    id: 1,
    title: 'Transparency is the cornerstone of trust.',
    content: (
      <Body mb={500}>
        We will be transparent about data collection and its use so you can make
        informed decisions. We will hold our partners and Customers accountable
        to ensure that 3rd parties that use HOVER to provide you services adhere
        to our privacy policies and terms of use. We will protect the data you
        entrust to us with strong security and encryption.
      </Body>
    ),
  },
  {
    id: 2,
    title: 'Your choices matter.',
    content: (
      <Body mb={500}>
        Whether you come to HOVER on your own or through a 3rd party, we’ll
        respect your choice of who has access to your personal data. You can
        provide or revoke access to any 3rd party, through your HOVER settings.
      </Body>
    ),
  },
];

export const TRUST_CENTER_SECURITY_AND_PRIVACY: Array<IFaqQuestion> = [
  {
    id: 0,
    title: 'Information Security Policy',
    content: (
      <Body mb={500}>
        Describes both acceptable and prohibited use of our networks, the
        Internet (including social media), laptops, and other devices. It also
        describes password and multi-factor authentication requirements.
      </Body>
    ),
  },
  {
    id: 1,
    title: 'Access Management',
    content: (
      <Body mb={500}>
        We practice the security principle of least privilege. We allow access
        to information and resources to only those who need it to perform their
        role. We keep records of all access requests and audit our accounts
        regularly.
      </Body>
    ),
  },
  {
    id: 2,
    title: 'Information Asset Management\n',
    content: (
      <Body mb={500}>
        Describes how we classify information at HOVER. These classifications
        govern the treatment of information assets, as we protect your data, as
        well as our intellectual property.
      </Body>
    ),
  },
];
